"use client";

import type { ReactNode } from "react";
import { useLocale, useTranslations } from "use-intl";

import {
  formatCompactCommercePrice,
  formatCommercePrice,
  isPriceDropAtLeastThreshold,
  offerMovementSignal,
} from "@/components/marketplace/deal-offers-utils";
import { offerPriceRangePosition } from "@/components/marketplace/offer-price-range-position";
import {
  PriceIndicatorChips,
  priceIndicatorTextClass,
} from "@/components/marketplace/price-indicator-chips";
import { Price } from "@/components/Price";
import { cn } from "@/lib/utils";

type MovementSource = Parameters<typeof offerMovementSignal>[0];
type PriceMovement = ReturnType<typeof offerMovementSignal>;

const ALBUM_CARD_REFERENCE_PRICE_MINIMUM_DROP = 0.05;

function numericPrice(value: string | null): number | null {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

type OfferPriceRangeScaleProps = {
  price: {
    currentPrice: string | null;
    dealReferencePrice: string | null;
  };
  displayPrice?: string | null;
  lowestPrice: string | null;
  averagePrice: string | null;
  highestPrice: string | null;
  currency: string | null;
  movementSource: MovementSource;
  priceIndicators: string[];
  compactSignal?: boolean;
  /** One row: lowest price, the bar with its pointer, highest price (the average stays in the aria label). */
  inlineRangeLabels?: boolean;
  streamlinedSignal?: boolean;
  subtleRangeBar?: boolean;
  showRangeBar?: boolean;
  showRangeLabels?: boolean;
  showReferencePrice?: boolean;
  /** When false, renders only the range bar and labels (callers show the price elsewhere). */
  showPriceSummary?: boolean;
  latestLabel?: string;
  showLatestLabel?: boolean;
  className?: string;
};

function shouldShowReferencePrice(input: {
  compactSignal: boolean;
  currentPrice: string | null;
  movement: PriceMovement;
  referencePrice: string | null;
  referenceValue: string | null;
  streamlinedSignal: boolean;
}) {
  if (!input.compactSignal && !input.streamlinedSignal) return false;
  if (input.movement?.kind !== "down" || input.movement.source !== "deal") return false;
  if (input.referenceValue === null) return false;
  return isPriceDropAtLeastThreshold({
    currentPrice: input.currentPrice,
    referencePrice: input.referencePrice,
    threshold: input.streamlinedSignal ? 0 : ALBUM_CARD_REFERENCE_PRICE_MINIMUM_DROP,
  });
}

function displayedPriceMovement(input: {
  compactSignal: boolean;
  indicatorTextClass: string | null;
  movement: PriceMovement;
  streamlinedSignal: boolean;
}) {
  const hidesFlatMovement =
    (input.compactSignal || input.streamlinedSignal) &&
    input.movement?.kind === "flat" &&
    input.indicatorTextClass;
  return hidesFlatMovement ? null : input.movement;
}

function priceMovementText(movement: PriceMovement) {
  if (!movement) return null;
  if (movement.kind === "flat") return movement.meta.label;
  return `${movement.meta.label} ${movement.percent}`;
}

function CurrentOfferPrice({
  compactSignal,
  latestLabel,
  latestValue,
  referenceValue,
  showReferencePrice,
  showLatestLabel,
}: {
  compactSignal: boolean;
  latestLabel: string;
  latestValue: ReactNode;
  referenceValue: ReactNode;
  showReferencePrice: boolean;
  showLatestLabel: boolean;
}) {
  return (
    <div>
      {showLatestLabel && (
        <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
          {latestLabel}
        </p>
      )}
      <div
        className={cn(
          "flex flex-wrap items-baseline gap-x-2 gap-y-1 font-black leading-none tabular-nums",
          compactSignal ? "text-lg" : "text-2xl",
        )}
      >
        {showReferencePrice && (
          <span
            className={cn(
              "font-normal text-muted-foreground line-through decoration-1",
              compactSignal ? "text-xs" : "text-sm",
            )}
          >
            {referenceValue}
          </span>
        )}
        <span>{latestValue}</span>
      </div>
    </div>
  );
}

function OfferPriceSignals({
  compactSignal,
  displayedMovement,
  indicatorTextClass,
  movementText,
  priceIndicators,
}: {
  compactSignal: boolean;
  displayedMovement: PriceMovement;
  indicatorTextClass: string | null;
  movementText: string | null;
  priceIndicators: string[];
}) {
  if (!compactSignal && !displayedMovement && !indicatorTextClass) return null;
  return (
    <div
      className={cn(
        "flex min-w-0 flex-wrap items-center gap-1.5 pb-0.5 text-xs font-black",
        compactSignal && "h-6 flex-nowrap gap-1 text-[10px]",
        indicatorTextClass ?? displayedMovement?.meta.className,
      )}
    >
      <PriceMovementDisplay
        compactSignal={compactSignal}
        movement={displayedMovement}
        movementText={movementText}
      />
      <PriceSignalSeparator
        hasIndicator={Boolean(indicatorTextClass)}
        hasMovement={Boolean(displayedMovement)}
      />
      <PriceIndicatorChips
        className={cn(
          "[&>span]:text-xs",
          compactSignal &&
            "flex-nowrap [&>span]:whitespace-nowrap [&>span]:px-0 [&>span]:text-[9px]",
        )}
        indicators={priceIndicators}
        size="responsive"
      />
    </div>
  );
}

function PriceMovementDisplay({
  compactSignal,
  movement,
  movementText,
}: {
  compactSignal: boolean;
  movement: PriceMovement;
  movementText: string | null;
}) {
  if (!movement) return null;
  const text = compactSignal && movement.kind !== "flat" ? movement.percent : movementText;
  const MovementIcon = movement.meta.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1",
        compactSignal && "shrink-0 whitespace-nowrap",
      )}
      title={movementText ?? undefined}
    >
      {compactSignal && <MovementIcon className="text-base leading-none" />}
      {text}
    </span>
  );
}

function PriceSignalSeparator({
  hasIndicator,
  hasMovement,
}: {
  hasIndicator: boolean;
  hasMovement: boolean;
}) {
  if (!hasIndicator || !hasMovement) return null;
  return <span aria-hidden="true">|</span>;
}

function OfferPriceSummaryRow({
  children,
  compactSignal,
  hidden,
}: {
  children: ReactNode;
  compactSignal: boolean;
  hidden: boolean;
}) {
  if (hidden) return null;
  return (
    <div
      className={cn(
        "flex flex-wrap items-end",
        compactSignal ? "flex-col items-start justify-start gap-0.5" : "justify-between gap-2",
      )}
    >
      {children}
    </div>
  );
}

function rangeBarSpacing(input: { compactSignal: boolean; inline: boolean; subtle: boolean }) {
  if (input.inline) return "min-w-0 flex-1";
  if (input.subtle) return "mt-3";
  return input.compactSignal ? "mt-4" : "mt-5";
}

function OfferPriceRangeBar({
  compactSignal,
  currentPosition,
  hasRange,
  inline = false,
  subtle,
}: {
  compactSignal: boolean;
  currentPosition: number;
  hasRange: boolean;
  inline?: boolean;
  subtle: boolean;
}) {
  return (
    <div className={cn("relative", rangeBarSpacing({ compactSignal, inline, subtle }))}>
      <div
        className={cn(
          "relative rounded-full",
          subtle ? "h-1 opacity-60" : "h-2",
          hasRange || subtle
            ? "bg-gradient-to-r from-emerald-500 via-amber-400 to-red-500"
            : "bg-muted",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "absolute size-0 -translate-x-1/2 border-x-transparent border-t-foreground",
            subtle
              ? "bottom-[calc(100%+1px)] border-x-[5px] border-t-[7px] opacity-75"
              : "bottom-[calc(100%+2px)] border-x-[7px] border-t-[10px] drop-shadow-sm",
          )}
          style={{ left: `${currentPosition}%` }}
        />
      </div>
    </div>
  );
}

type PriceRangeLabel = {
  align: string;
  label: string;
  value: string | null;
  display: ReactNode;
};

function InlineRangeLabel({ label }: { label: PriceRangeLabel | undefined }) {
  if (!label) return null;
  return (
    <span className="shrink-0" title={`${label.label}: ${label.value ?? "—"}`}>
      {label.display}
    </span>
  );
}

/** Lowest and highest price at the ends of the bar, on one line. */
function OfferPriceInlineRange({ bar, labels }: { bar: ReactNode; labels: PriceRangeLabel[] }) {
  return (
    <div className="mt-2 flex items-center gap-2.5 text-[11px] tabular-nums text-muted-foreground">
      <InlineRangeLabel label={labels[0]} />
      {bar}
      <InlineRangeLabel label={labels[labels.length - 1]} />
    </div>
  );
}

function OfferPriceRangeLabels({ labels }: { labels: PriceRangeLabel[] }) {
  return (
    <div className="mt-2 grid grid-cols-3 gap-2">
      {labels.map(({ label, display, align }) => (
        <div key={label} className={align}>
          <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <p className="mt-0.5 text-xs font-black tabular-nums sm:text-sm">{display}</p>
        </div>
      ))}
    </div>
  );
}

/** The range bar plus its labels: stacked under the bar, or inline at its ends. */
function OfferPriceRange({
  compactSignal,
  currentPosition,
  hasRange,
  inlineLabels,
  labels,
  showBar,
  showLabels,
  subtle,
}: {
  compactSignal: boolean;
  currentPosition: number;
  hasRange: boolean;
  inlineLabels: boolean;
  labels: PriceRangeLabel[];
  showBar: boolean;
  showLabels: boolean;
  subtle: boolean;
}) {
  const bar = (inline: boolean) => (
    <OfferPriceRangeBar
      compactSignal={compactSignal}
      currentPosition={currentPosition}
      hasRange={hasRange}
      inline={inline}
      subtle={subtle}
    />
  );
  if (showBar && inlineLabels) {
    return <OfferPriceInlineRange labels={showLabels ? labels : []} bar={bar(true)} />;
  }

  return (
    <>
      {showBar && bar(false)}
      {showLabels && <OfferPriceRangeLabels labels={labels} />}
    </>
  );
}

function offerPriceRangeMetrics(
  input: Pick<OfferPriceRangeScaleProps, "averagePrice" | "highestPrice" | "lowestPrice" | "price">,
) {
  const latest = numericPrice(input.price.currentPrice);
  const lowest = numericPrice(input.lowestPrice);
  const highest = numericPrice(input.highestPrice);
  return {
    currentPosition: offerPriceRangePosition({
      current: latest,
      minimum: lowest,
      average: numericPrice(input.averagePrice),
      maximum: highest,
    }),
    hasRange: lowest != null && highest != null && highest > lowest,
  };
}

function offerPriceRangeLabels(input: {
  averagePrice: string | null;
  currency: string | null;
  highestPrice: string | null;
  locale: string;
  lowestPrice: string | null;
  t: (key: "lowestPrice" | "averagePrice" | "highestPrice") => string;
}): PriceRangeLabel[] {
  const rangeLabel = (
    key: "lowestPrice" | "averagePrice" | "highestPrice",
    amount: string | null,
    align: string,
  ): PriceRangeLabel => ({
    label: input.t(key),
    value: formatCommercePrice(amount, input.currency, input.locale),
    display: <Price amount={amount || null} currency={input.currency} fallback="—" plain />,
    align,
  });
  return [
    rangeLabel("lowestPrice", input.lowestPrice, "text-left"),
    rangeLabel("averagePrice", input.averagePrice, "text-center"),
    rangeLabel("highestPrice", input.highestPrice, "text-right"),
  ];
}

function displayedPriceValues(input: {
  compactSignal: boolean;
  currency: string | null;
  currentPrice: string | null;
  displayPrice?: string | null;
  locale: string;
  referencePrice: string | null;
}) {
  const formatter = input.compactSignal ? formatCompactCommercePrice : formatCommercePrice;
  const display = (amount: string | null | undefined, plain = false) =>
    formatter(amount, input.currency, input.locale) ? (
      <Price
        amount={amount}
        currency={input.currency}
        compact={input.compactSignal}
        plain={plain}
      />
    ) : null;
  const latestAmount = input.displayPrice ?? input.currentPrice;
  return {
    latestValue: formatter(latestAmount, input.currency, input.locale) ?? "—",
    latestDisplay: display(latestAmount) ?? "—",
    referenceValue: formatter(input.referencePrice, input.currency, input.locale),
    // The struck reference price is text-xs/text-sm, too small for superscripts.
    referenceDisplay: display(input.referencePrice, true),
  };
}

function allowsLastChangeFallback(compactSignal: boolean, streamlinedSignal: boolean) {
  return !compactSignal && !streamlinedSignal;
}

function offerPriceDisplayOptions(input: {
  compactSignal: boolean;
  latestLabel?: string;
  showLatestLabel?: boolean;
  showReferencePrice?: boolean;
  streamlinedSignal: boolean;
  translatedLatestLabel: string;
}) {
  return {
    allowReferencePrice: input.showReferencePrice ?? true,
    latestLabel: input.latestLabel ?? input.translatedLatestLabel,
    showLatestLabel: input.showLatestLabel ?? (!input.compactSignal && !input.streamlinedSignal),
  };
}

function isReferencePriceVisible(
  allowReferencePrice: boolean,
  input: Parameters<typeof shouldShowReferencePrice>[0],
) {
  if (!allowReferencePrice) return false;
  return shouldShowReferencePrice(input);
}

function offerPriceRangeAriaLabel(input: {
  labels: PriceRangeLabel[];
  latestLabel: string;
  latestValue: string;
  showRangeLabels: boolean;
}) {
  const latest = `${input.latestLabel}: ${input.latestValue}`;
  if (!input.showRangeLabels) return latest;
  return `${latest}. ${input.labels
    .map(({ label, value }) => `${label}: ${value ?? "—"}`)
    .join(". ")}`;
}

export function OfferPriceRangeScale({
  price,
  displayPrice,
  lowestPrice,
  averagePrice,
  highestPrice,
  currency,
  movementSource,
  priceIndicators,
  compactSignal = false,
  inlineRangeLabels = false,
  streamlinedSignal = false,
  subtleRangeBar,
  showRangeBar = true,
  showRangeLabels = true,
  showReferencePrice,
  showPriceSummary,
  latestLabel,
  showLatestLabel,
  className,
}: OfferPriceRangeScaleProps) {
  const locale = useLocale();
  const t = useTranslations("shared.affiliates");
  const displayOptions = offerPriceDisplayOptions({
    compactSignal,
    latestLabel,
    showLatestLabel,
    showReferencePrice,
    streamlinedSignal,
    translatedLatestLabel: t("latestPrice"),
  });
  const { currentPosition, hasRange } = offerPriceRangeMetrics({
    averagePrice,
    highestPrice,
    lowestPrice,
    price,
  });
  const { latestValue, latestDisplay, referenceValue, referenceDisplay } = displayedPriceValues({
    compactSignal,
    currency,
    currentPrice: price.currentPrice,
    displayPrice,
    locale,
    referencePrice: price.dealReferencePrice,
  });
  const movement = offerMovementSignal(movementSource, locale, t, {
    allowLastChangeFallback: allowsLastChangeFallback(compactSignal, streamlinedSignal),
  });
  const referencePriceVisible = isReferencePriceVisible(displayOptions.allowReferencePrice, {
    compactSignal,
    currentPrice: price.currentPrice,
    movement,
    referencePrice: price.dealReferencePrice,
    referenceValue,
    streamlinedSignal,
  });
  const indicatorTextClass = priceIndicatorTextClass(priceIndicators);
  const displayedMovement = displayedPriceMovement({
    compactSignal,
    indicatorTextClass,
    movement,
    streamlinedSignal,
  });
  const movementText = priceMovementText(displayedMovement);
  const labels = offerPriceRangeLabels({
    averagePrice,
    currency,
    highestPrice,
    locale,
    lowestPrice,
    t,
  });
  const ariaLabel = offerPriceRangeAriaLabel({
    labels,
    latestLabel: displayOptions.latestLabel,
    latestValue,
    showRangeLabels,
  });

  return (
    <div
      className={cn("rounded-xl bg-card", compactSignal ? "py-1" : "px-3 py-2.5", className)}
      role="img"
      aria-label={ariaLabel}
    >
      <OfferPriceSummaryRow compactSignal={compactSignal} hidden={showPriceSummary === false}>
        <CurrentOfferPrice
          compactSignal={compactSignal}
          latestLabel={displayOptions.latestLabel}
          latestValue={latestDisplay}
          referenceValue={referenceDisplay}
          showReferencePrice={referencePriceVisible}
          showLatestLabel={displayOptions.showLatestLabel}
        />
        <OfferPriceSignals
          compactSignal={compactSignal}
          displayedMovement={displayedMovement}
          indicatorTextClass={indicatorTextClass}
          movementText={movementText}
          priceIndicators={priceIndicators}
        />
      </OfferPriceSummaryRow>
      <OfferPriceRange
        compactSignal={compactSignal}
        currentPosition={currentPosition}
        hasRange={hasRange}
        inlineLabels={inlineRangeLabels}
        labels={labels}
        showBar={showRangeBar}
        showLabels={showRangeLabels}
        subtle={Boolean(subtleRangeBar)}
      />
    </div>
  );
}
